import { ActionContext, ActionTree } from 'vuex';
import { RootState } from '@/interface/store/';
import * as s_Interface from '@/interface/store/request';
import * as c_Interface from '@/interface/components/request';
import { ExportData, File, FilePayload } from '@/interface/components';
import * as API from '@/api/request';

export const actions: ActionTree<s_Interface.Request, RootState> = {
  async getRequests(
    { commit }: ActionContext<s_Interface.Request, RootState>,
    {
      groupId,
      attributes,
      include,
      offset,
      limit,
      order,
      status,
      filters,
      query,
      hasDropdown,
    }: {
      groupId: string;
      attributes: string;
      include: string;
      offset: number;
      limit: number;
      order: string;
      status: { status: Array<string> };
      filters: s_Interface.RequestFilter;
      query: string;
      hasDropdown: boolean;
    }
  ) {
    try {
      const { data } = await API.getRequests(
        groupId,
        attributes,
        include,
        offset,
        limit,
        order,
        status,
        filters,
        query,
        hasDropdown
      );
      commit('SET_REQUESTS', data);
      const newStatus = Array.isArray(status.status) ? status.status.join() : status.status;
      commit('UPDATE_QUERY_PARAMS', {
        status: newStatus,
        limit,
        offset,
        order,
        hasDropdown,
      });
    } catch (e) {
      throw e;
    }
  },
  async getMobileRequests(
    { commit }: ActionContext<s_Interface.Request, RootState>,
    {
      groupId,
      attributes,
      include,
      offset,
      limit,
      order,
      status,
      filters,
      query,
      hasDropdown,
    }: {
      groupId: string;
      attributes: string;
      include: string;
      offset: number;
      limit: number;
      order: string;
      status: { status: Array<string> };
      filters: s_Interface.RequestFilter;
      query: string;
      hasDropdown: boolean;
    }
  ) {
    try {
      const { data } = await API.getRequests(
        groupId,
        attributes,
        include,
        offset,
        limit,
        order,
        status,
        filters,
        query,
        hasDropdown
      );
      commit('SET_MOBILE_REQUESTS', data);
      const newStatus = Array.isArray(status.status) ? status.status.join() : status.status;
      commit('UPDATE_QUERY_PARAMS', {
        status: newStatus,
        limit,
        offset,
        order,
        hasDropdown,
      });
    } catch (e) {
      throw e;
    }
  },
  async setRequestBulk(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.RequestBulkPayload[] }
  ) {
    try {
      const { data } = await API.setRequestBulk(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async setRequest(
    { commit },
    {
      groupId,
      payload,
      files,
    }: { groupId: string; payload: c_Interface.RequestItemPayload; files: any }
  ) {
    try {
      const { data } = await API.setRequest(groupId, payload);
      const requestId = data.id;
      const filesPayload = files.map((file: File) => {
        return {
          Bucket: 'images.labsby.com',
          Key: `groups/${groupId}/requests/${requestId}/attachments/${file.name}`,
        };
      });
      const result = await API.attachmentsRequestSignUrl(groupId, requestId, filesPayload);
      result.data.url.forEach((url: string, i: number) => {
        API.attachmentsRequestUpload(url, files[i]);
      });
      const insertDataPayload = files.map((file: any) => {
        return {
          url: `https://images.labsby.com/groups/${groupId}/requests/${requestId}/attachments/${file.name}`,
          fileName: file.name,
        };
      });
      await API.setAttachment(groupId, requestId, insertDataPayload);
    } catch (e) {
      throw e;
    }
  },
  async setAttachment(
    { commit },
    { groupId, requestId, files }: { groupId: string; requestId: string; files: any }
  ) {
    try {
      const filesPayload = files.map((file: File) => {
        return {
          Bucket: 'images.labsby.com',
          Key: `groups/${groupId}/requests/${requestId}/attachments/${file.name}`,
        };
      });
      const result: any = await API.attachmentsRequestSignUrl(groupId, requestId, filesPayload);
      result.data.url.forEach((url: string, i: number) => {
        API.attachmentsRequestUpload(url, files[i]);
      });
      const insertDataPayload = files.map((file: File) => {
        return {
          url: `https://images.labsby.com/groups/${groupId}/requests/${requestId}/attachments/${file.name}`,
          fileName: file.name,
        };
      });
      const { data } = await API.setAttachment(groupId, requestId, insertDataPayload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getAttachments(
    { commit }: ActionContext<s_Interface.Request, RootState>,
    { groupId, requestId }: { groupId: string; requestId: string }
  ) {
    try {
      const { data } = await API.getAttachments(groupId, requestId);
      commit('SET_REQUEST_ATTACHMENTS', data);
    } catch (e) {
      throw e;
    }
  },
  async deleteAttachment(
    { commit },
    {
      groupId,
      requestId,
      attachmentId,
    }: { groupId: string; requestId: string; attachmentId: string }
  ) {
    try {
      await API.deleteAttachment(groupId, requestId, attachmentId);
    } catch (e) {
      throw e;
    }
  },
  async deleteRequests(
    { commit },
    { groupId, payload }: { groupId: string; payload: Array<string> }
  ) {
    try {
      const { data } = await API.deleteRequests(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async bulkSetRequest(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.RequestBulkPayload[] }
  ) {
    try {
      const { data } = await API.setRequestBulk(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async updateRequestStatus(
    { commit },
    { groupId, payload }: { groupId: string; payload: s_Interface.RequestItem }
  ) {
    try {
      const { data } = await API.requestStatusChange(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async getExportRequest(
    { commit },
    { groupId, startAt, endAt }: { groupId: string; startAt: string; endAt: string }
  ): Promise<ExportData> {
    try {
      const { data } = await API.exportRequest(groupId, startAt, endAt);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getTemplateRequest({ commit }, groupId: string) {
    try {
      const { data } = await API.getTemplateRequest(groupId);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getRequest({ commit }, { groupId, requestId }: { groupId: string; requestId: string }) {
    try {
      const { data } = await API.getRequest(groupId, requestId);
      commit('SET_REQUEST', data);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async updateRequest(
    { commit },
    {
      groupId,
      requestId,
      payload,
    }: { groupId: string; requestId: string; payload: { status: string } }
  ) {
    try {
      const { data } = await API.updateRequest(groupId, requestId, payload);
    } catch (e) {
      throw e;
    }
  },
  async bulkUpdateRequest(
    { commit },
    { groupId, payload }: { groupId: string; payload: { id: string; fkPurchaseOrderId: string }[] }
  ) {
    try {
      const { data } = await API.bulkUpdateRequest(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async getPurchaseOrders({ commit }, { groupId }: { groupId: string }) {
    try {
      const { data } = await API.getPurchaseOrders(groupId);
      commit('SET_PO', data);
    } catch (e) {
      throw e;
    }
  },
  async setPurchaseOrder(
    { commit },
    { groupId, poName }: { groupId: string; poName: { name: string } }
  ) {
    try {
      const { data } = await API.setPurchaseOrder(groupId, poName);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async signUrlRequestImport({ commit }, { groupId, file }: { groupId: string; file: any }) {
    try {
      const payload = {
        Key: `groups/${groupId}/imports/${file.name}`,
        Bucket: `images.labsby.com`,
      };
      const { data } = await API.signUrlRequestImport(groupId, payload);
      const result = await API.requestImport(data.url, file);
    } catch (e) {
      throw e;
    }
  },
  async requestUpload({ commit }, uploadPayload: { groupId: string; payload: FilePayload }) {
    try {
      const { data } = await API.requestUpload(uploadPayload.groupId, uploadPayload.payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async poSignURI(
    { commit },
    {
      groupId,
      purchaseOrderId,
      files,
    }: { groupId: string; purchaseOrderId: string; files: Array<any> }
  ) {
    try {
      if (files.length > 0) {
        const payload = files.map((v: any) => {
          return {
            Bucket: 'images.labsby.com',
            Key: `inventories/attachments/${v.name}`,
          };
        });

        const { data } = await API.poSignURI(groupId, purchaseOrderId, payload);
        await data.url.forEach((url: string, i: number) => {
          API.setS3PoAttachment(url, files[i]);
        });
      }
    } catch (e) {
      throw e;
    }
  },
  async setPoAttachment(
    { commit },
    {
      groupId,
      purchaseOrderId,
      files,
    }: { groupId: string; purchaseOrderId: string; files: Array<any> }
  ) {
    try {
      const filesPayload = files.map((file: File) => {
        return {
          Bucket: 'images.labsby.com',
          Key: `groups/${groupId}/purchase-orders/${purchaseOrderId}/attachments/${file.name}`,
        };
      });
      const result = await API.poSignURI(groupId, purchaseOrderId, filesPayload);
      result.data.url.forEach((url: string, i: number) => {
        API.setS3PoAttachment(url, files[i]);
      });
      const insertDataPayload = files.map((file: any) => {
        return {
          url: `https://images.labsby.com/groups/${groupId}/purchase-orders/${purchaseOrderId}/attachments/${file.name}`,
          fileName: file.name,
        };
      });
      const { data } = await API.setPoAttachment(groupId, purchaseOrderId, insertDataPayload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async setComment(
    { commit },
    {
      groupId,
      requestId,
      payload,
    }: { groupId: string; requestId: string; payload: { body: string } }
  ): Promise<s_Interface.RequestComment> {
    try {
      const { data } = await API.setComment(groupId, requestId, payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getComments(
    { commit },
    { groupId, requestId }: { groupId: string; requestId: string }
  ): Promise<Array<s_Interface.RequestComment>> {
    try {
      const { data } = await API.getComments(groupId, requestId);
      commit('SET_REQUEST_COMMENTS', data);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async deleteComment(
    { commit },
    { groupId, requestId, commentId }: { groupId: string; requestId: string; commentId: string }
  ) {
    try {
      const { data } = await API.deleteComment(groupId, requestId, commentId);
    } catch (e) {
      throw e;
    }
  },
  async updateComment(
    { commit },
    {
      groupId,
      requestId,
      commentId,
      payload,
    }: { groupId: string; requestId: string; commentId: string; payload: { body: string } }
  ) {
    try {
      const { data } = await API.updateComment(groupId, requestId, commentId, payload);
    } catch (e) {
      throw e;
    }
  },
  async getRequestHistories(
    { commit },
    { groupId, requestId }: { groupId: string; requestId: string }
  ) {
    try {
      const { data } = await API.getRequestHistories(groupId, requestId);
      commit('SET_REQUEST_HISTORIES', data);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getQueryParams({ commit }, { groupId }: { groupId: string }) {
    try {
      const { data } = await API.getQueryParams(groupId);
      commit('SET_QUERY_PARAMS', data);
    } catch (e) {
      throw e;
    }
  },
  async updateQueryParams({ commit }, { groupId, payload }) {
    try {
      await API.updateQueryParams(groupId, payload);
      commit('UPDATE_QUERY_PARAMS', payload);
    } catch (e) {
      throw e;
    }
  },
};
